import { Popover as Primitive } from "radix-ui";
import { useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { initialActive, matchOptions } from "../../lib/combobox";
import type { ComboboxOption } from "../../lib/combobox";
import { stepIndex } from "../../lib/spotlight";

/**
 * A searchable select: a chip that opens a floating list with a filter box on top.
 *
 * ## Why not `<select>`
 *
 * A native list cannot be filtered, and its open state is drawn by the OS — on a
 * desktop a grey system menu over a page of paper. This one types to narrow and
 * looks like the page it sits on.
 *
 * ## What it borrows
 *
 * The open/close, focus return, outside-click and Escape handling are Radix
 * Popover's (`ui/Popover.tsx` says why that is not hand-rolled). The keyboard
 * model is Spotlight's: focus stays in the filter box, which is a `combobox`
 * whose highlighted row is announced through `aria-activedescendant`, and the
 * arrows wrap (`lib/spotlight.ts#stepIndex`).
 */
export function Combobox({
  label,
  value,
  options,
  onChange,
  icon,
  searchPlaceholder = "Search",
}: {
  /** The control's name, spoken before the current choice: "Window: Today". */
  readonly label: string;
  readonly value: string;
  readonly options: readonly ComboboxOption[];
  readonly onChange: (value: string) => void;
  readonly icon?: ReactNode;
  readonly searchPlaceholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.value === value) ?? options[0];

  return (
    <Primitive.Root open={open} onOpenChange={setOpen}>
      <Primitive.Trigger asChild>
        <button
          type="button"
          className={[
            "group inline-flex h-9 max-w-full items-center gap-2 rounded-full border border-rule bg-paper-raised pr-3 pl-3.5",
            "text-data font-medium text-ink",
            "transition-colors duration-fast ease-swiss hover:border-ink-faint",
            "data-[state=open]:border-ink",
          ].join(" ")}
        >
          {icon === undefined ? null : (
            <span aria-hidden="true" className="text-ink-faint">
              {icon}
            </span>
          )}
          <span className="sr-only">{label}: </span>
          <span className="truncate">{selected?.label ?? "—"}</span>
          <Chevron />
        </button>
      </Primitive.Trigger>
      <Primitive.Portal>
        <Primitive.Content
          aria-label={`${label} options`}
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className={[
            "z-50 w-(--radix-popover-trigger-width) min-w-60 max-w-[calc(100vw-24px)] overflow-hidden",
            "rounded-item border border-rule bg-paper-raised shadow-float",
            "origin-(--radix-popover-content-transform-origin)",
            "data-[state=open]:animate-[stage-rise_var(--duration-fast)_var(--ease-swiss)]",
          ].join(" ")}
        >
          <OptionList
            label={label}
            value={value}
            options={options}
            searchPlaceholder={searchPlaceholder}
            onPick={(next) => {
              setOpen(false);
              if (next !== value) onChange(next);
            }}
          />
        </Primitive.Content>
      </Primitive.Portal>
    </Primitive.Root>
  );
}

/**
 * The open panel's body. Its own component so the query and the highlight are
 * born fresh each time the list opens, rather than reset by an effect.
 */
function OptionList({
  label,
  value,
  options,
  searchPlaceholder,
  onPick,
}: {
  readonly label: string;
  readonly value: string;
  readonly options: readonly ComboboxOption[];
  readonly searchPlaceholder: string;
  readonly onPick: (value: string) => void;
}) {
  const listId = useId();
  const [query, setQuery] = useState("");
  const matches = matchOptions(options, query);
  const [active, setActive] = useState(() => initialActive(matches, value));
  const listRef = useRef<HTMLUListElement>(null);

  // A new query re-points the highlight at what is now listed.
  const shown = active >= matches.length ? initialActive(matches, value) : active;
  const optionId = (index: number) => `${listId}-option-${index}`;

  useEffect(() => {
    if (shown === -1) return;
    listRef.current?.querySelector(`[data-index="${shown}"]`)?.scrollIntoView({ block: "nearest" });
  }, [shown]);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // While an IME is composing (a Khmer or CJK keyboard) the arrows walk its
    // candidate list and Enter commits text; neither is ours. Safari reports the
    // committing Enter after `compositionend`, recognisable only by keyCode 229.
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setActive(stepIndex(shown, event.key === "ArrowDown" ? 1 : -1, matches.length));
      return;
    }
    const choice = matches[shown];
    if (event.key === "Enter" && choice !== undefined) {
      event.preventDefault();
      onPick(choice.value);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 border-b border-rule px-3">
        <SearchGlyph />
        <input
          type="text"
          role="combobox"
          aria-label={`Search ${label.toLowerCase()}`}
          aria-expanded="true"
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={shown === -1 ? undefined : optionId(shown)}
          autoComplete="off"
          spellCheck={false}
          placeholder={searchPlaceholder}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(initialActive(matchOptions(options, event.target.value), value));
          }}
          onKeyDown={onKeyDown}
          className="combobox-search h-10 w-full bg-transparent text-data text-ink placeholder:text-ink-faint"
        />
      </div>
      <ul
        ref={listRef}
        id={listId}
        role="listbox"
        aria-label={label}
        className="m-0 max-h-64 list-none overflow-y-auto p-1.5"
      >
        {matches.map((option, index) => {
          const isSelected = option.value === value;
          return (
            <li
              key={option.value}
              id={optionId(index)}
              data-index={index}
              role="option"
              aria-selected={isSelected}
              // Pointer-down, not click, so the filter box keeps focus until the pick.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onPick(option.value)}
              // Guarded: scrolling a row under a still pointer fires a synthetic
              // move, which would drag the keyboard's highlight back to the mouse.
              onMouseMove={() => {
                if (index !== shown) setActive(index);
              }}
              className={[
                "flex cursor-default items-center justify-between gap-3 rounded-hairline px-2.5 py-1.5 text-data",
                index === shown ? "bg-paper-sunken text-ink" : "text-ink-muted",
                isSelected ? "font-semibold text-ink" : "",
              ].join(" ")}
            >
              <span className="truncate">{option.label}</span>
              {isSelected ? <Check /> : null}
            </li>
          );
        })}
      </ul>
      {/* Outside the listbox, which may hold only options, and live so a screen
          reader hears the filter come up empty. */}
      <p role="status" className="m-0 px-4 pb-3 text-data text-ink-faint empty:hidden">
        {matches.length === 0 ? "No matches" : ""}
      </p>
    </>
  );
}

function Chevron() {
  return (
    <svg
      viewBox="0 0 12 12"
      width="12"
      height="12"
      fill="none"
      aria-hidden="true"
      className="shrink-0 text-ink-faint transition-transform duration-fast ease-swiss group-data-[state=open]:rotate-180"
    >
      <path
        d="M3 4.5 6 7.5 9 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Check() {
  return (
    <svg
      viewBox="0 0 12 12"
      width="12"
      height="12"
      fill="none"
      aria-hidden="true"
      className="shrink-0 text-ink"
    >
      <path
        d="M2.5 6.5 5 9 9.5 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SearchGlyph() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      aria-hidden="true"
      className="shrink-0 text-ink-faint"
    >
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="m10.5 10.5 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
